// @ts-strict-ignore
import {StackableComponent, Visibility} from 'common/components/base/stackable';
// eslint-disable-next-line import/no-cycle
import {confirm} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {portalify} from 'common/util/experimental-portal';
import {ModalChangeEvent} from 'common/components/modal/types';
import {IronContextRegistry} from 'common/report/context-registry';

import {template} from './template';
import css from './index.inline.styl';

export enum ModalType {
  Popup = `popup`,
  Modal = `modal`,
  RightDrawer = `right-drawer`,
  BottomDrawer = `bottom-drawer`,
  TourModal = `tour-modal`,
}

export enum VerticalAlignment {
  Top = `top`,
  Center = `center`,
  Bottom = `bottom`,
}

export interface ModalAttrs {
  closeable?: boolean;
  'grow-to-content'?: boolean;
  'hide-backdrop'?: boolean;
  'hide-background'?: boolean;
  'hide-close-button'?: boolean;
  'modal-type'?: ModalType;
  'modal-title'?: string;
  'modal-description'?: string;
  'not-fullscreen'?: boolean;
  open: boolean;
  'vertical-alignment'?: VerticalAlignment;
  'close-confirm-text'?: string;
  'sliding-transition'?: boolean;
  'close-button-padded'?: boolean;
}

export interface ModalJsxProps {
  attrs?: ModalAttrs;
  on?: {
    change?: (ev: ModalChangeEvent) => void;
    'stackable-closed'?: (ev: CustomEvent<{target: string; stackSize: number}>) => void;
    'stackable-open'?: (ev: CustomEvent<{target: string; stackSize: number}>) => void;
  };
}

/**
 * Defines space allocated for the scrollbar when a
 * modal + backdrop comes in and out of view
 */
const SCROLLBAR_WIDTH_CSS_VARIABLE = `--content-scrollbar-width`;
// Track how many modals have enabled/disabled document scroll as to not interfere with other modals
let documentScrollDisabledCount = 0;

export class Modal extends StackableComponent<EmptyObj, ModalAttrs, IronContextRegistry> {
  private disabledScroll = false;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      closeable: `boolean`,
      'grow-to-content': `boolean`,
      'hide-close-button': `boolean`,
      'hide-backdrop': `boolean`,
      'hide-background': `boolean`,
      'modal-type': {type: `string`, default: ModalType.Modal, enum: Object.values(ModalType)},
      'modal-title': `string`,
      'modal-description': `string`,
      'not-fullscreen': `boolean`,
      open: `boolean`,
      'vertical-alignment': {type: `string`, default: VerticalAlignment.Center, enum: Object.values(VerticalAlignment)},
      'close-confirm-text': `string`,
      'sliding-transition': `boolean`,
      'close-button-padded': `boolean`,
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      helpers: {
        ModalType,
        VerticalAlignment,
        isClosed: () => this.state.visibility === Visibility.Closed,
        showBackdrop: () =>
          [ModalType.Modal, ModalType.RightDrawer, ModalType.BottomDrawer].includes(this.attr(`modal-type`)) &&
          !this.attr(`hide-backdrop`),
        handleCloseClick: () => this.close(),
        handleBackdropClick: () => {
          if (this.attr(`closeable`)) {
            this.close();
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`open`)) {
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // only re-enable document scrolling if this modal disabled it and hasn't been closed already
    if (this.disabledScroll && this.state.visibility !== Visibility.Closed) {
      this.setDocumentScroll(true);
    }
  }

  attributeChangedCallback(attr, newValue, oldValue) {
    super.attributeChangedCallback(attr, newValue, oldValue);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        super.close();
      }
    }
  }

  get shouldCloseWhenClickedOutside() {
    return false;
  }

  async close() {
    if (!this.attr(`close-confirm-text`)) {
      super.close();
      return;
    }
    const shouldClose = await confirm(
      {
        description: this.attr(`close-confirm-text`),
        theme: ConfirmTheme.DELETE,
      },
      {},
      {
        title: ({t}) => t(`components.modal.close-window`),
        confirmButtonLabel: ({t}) => t(`components.modal.close`),
      },
    );
    if (shouldClose) {
      super.close();
    }
  }

  protected isCloseable() {
    return this.attr(`closeable`);
  }

  // TODO: only modals that set `closeable` deal with the change event, standarize
  // mp-modal usage to always handle the change event
  get shouldCloseOnEsc() {
    return this.attr(`closeable`);
  }

  /** Toggles document scrolling while preventing content shift */
  setDocumentScroll(enabled: boolean) {
    if (this.helpers.showBackdrop()) {
      if (enabled) {
        if (documentScrollDisabledCount === 1) {
          document.body.style.removeProperty(SCROLLBAR_WIDTH_CSS_VARIABLE);
          document.body.classList.remove(`on-modal`);
        }
        documentScrollDisabledCount -= 1;
        documentScrollDisabledCount = Math.max(documentScrollDisabledCount, 0);
      } else {
        this.disabledScroll = true;
        documentScrollDisabledCount += 1;
        if (documentScrollDisabledCount === 1) {
          const documentScrollbarWidth = `${window.innerWidth - document.body.clientWidth}px`;
          document.body.style.setProperty(SCROLLBAR_WIDTH_CSS_VARIABLE, documentScrollbarWidth);
          document.body.classList.add(`on-modal`);
        }
      }
    }
  }

  protected setVisibility(visibility: Visibility) {
    super.setVisibility(visibility);

    switch (visibility) {
      case Visibility.Open:
        this.setDocumentScroll(false);
        break;
      case Visibility.Closed:
        this.setDocumentScroll(true);
        break;
      default:
        break;
    }
  }
}

export const ModalEl = defineAndMakeJsxFactory<ModalJsxProps>(`mp-modal`, Modal);
export const ModalReact = panel2react<ModalJsxProps>(`mp-modal`, Modal);
export const ExperimentalModalEl = portalify(ModalEl);
